// @flow

import React from "react";
import { connect } from "react-redux";
import { localize } from "redux-i18n";
import { compose } from "redux";
import { route } from "@routes/helper";
import { push } from "connected-react-router";
import { GSGrid, SectionComponent } from "@components";
import type { State } from "@store/reducers";
import * as serviceSelectors from "@modules/service/selectors";
import { formatDateTime } from "@transformers/date";
import type { DefaultStandardScript } from "@modules/service/reducer/default-standard-scripts/types";
import { formatAuthoredBy } from "@transformers/names";
import actionCreators from "@modules/service/actionCreator";
import { DefaultStandardScriptEditModal } from "@modules/service/components";

type PropsFromState = $Exact<{
  list: ?Array<DefaultStandardScript>,
  draft: ?DefaultStandardScript,
  data: ?DefaultStandardScript,
  showEditModal: boolean,
}>;

type PropsFromDispatch = $Exact<{
  toggleEditDefaultStandardScriptModal: typeof actionCreators.ToggleEditDefaultStandardScriptModal,
  updateDefaultStandardScriptDraft: typeof actionCreators.UpdateDefaultStandardScriptDraft,
  updateDraft: typeof actionCreators.UpdateDefaultStandardScript,
  getDefaultStandardScriptContainer: typeof actionCreators.GetDefaultStandardScript,
}>;

type Props = $Exact<{
  clientId: number,
  ...PropsFromState,
  ...PropsFromDispatch,
  ...PropsFromLocalize,
}>;

export class DefaultScriptsSection extends React.Component<Props> {
  get data() {
    return this.props.list || [];
  }

  get columns() {
    return [
      {
        key: "scriptType",
        name: this.props.t("SCRIPT_TYPE"),
        align: "left",
        renderCell: ({ row }: { row: DefaultStandardScript }) => row.content.masterScript.name,
      },
      {
        key: "scriptText",
        name: this.props.t("SCRIPT_TEXT"),
        cellClass: "p-0",
        renderCell: ({ row }: { row: DefaultStandardScript }) => (
          <textarea
            disabled
            rows="3"
            maxLength="600"
            className="no-resize background-color-white borders-solid-color-white fs-14 line-height-20"
            defaultValue={row.content.scriptText}
          />
        ),
      },
      {
        key: "lastUpdated",
        name: this.props.t("LAST_UPDATED"),
        renderCell: ({ row }: { row: DefaultStandardScript }) => formatDateTime(row.updatedAt),
      },
      {
        key: "updatedBy",
        align: "left",
        name: this.props.t("UPDATED_BY"),
        renderCell: ({ row }: { row: DefaultStandardScript }) => formatAuthoredBy(row.updatedBy),
      },
      {
        key: "actions",
        name: this.props.t("EDIT"),
        align: "center",
        width: 50,
        renderCell: ({ row }: { row: DefaultStandardScript }) => {
          return (
            <span className="material-icons pointer" onClick={this.handleEditDraft(row)}>
              edit
            </span>
          );
        },
      },
    ];
  }

  handleEditDraft = (item: DefaultStandardScript) => () => {
    this.props.getDefaultStandardScriptContainer({
      clientId: this.props.clientId,
      stableId: item.id,
    });
    this.props.updateDefaultStandardScriptDraft(item);
    this.props.toggleEditDefaultStandardScriptModal(true);
  };

  handleModalClose = () => {
    this.props.toggleEditDefaultStandardScriptModal(false);
  };

  updateDraft = (updatedDraft: DefaultStandardScript) => {
    if (this.props.data && this.props.draft) {
      this.props.updateDraft({
        clientId: this.props.data.clientId,
        stableId: this.props.data.id,
        draft: updatedDraft.content,
      });
    }
  };

  render() {
    return (
      <SectionComponent title={<h5>{this.props.t("DEFAULT_SCRIPTS")}</h5>}>
        <div className="mb-5 mt-4 flex-1 d-flex">
          <GSGrid
            columns={this.columns}
            rowHeight={row => {
              return 80;
            }}
            rows={this.data}
          />
          {this.props.showEditModal && this.props.draft && (
            <DefaultStandardScriptEditModal
              isOpen={true}
              onCancel={this.handleModalClose}
              onSubmit={this.updateDraft}
              formData={this.props.draft}
            />
          )}
        </div>
      </SectionComponent>
    );
  }
}

const mapStateToProps = (state: State, ownProps: Object): PropsFromState => {
  return {
    list: serviceSelectors.getDefaultStandardScriptsList(state),
    draft: serviceSelectors.getDefaultStandardScriptDraft(state),
    showEditModal: serviceSelectors.getShowDefaultStandardScriptEditModal(state),
    data: serviceSelectors.currentDefaultStandardScriptContainer(state),
  };
};

const mapDispatchToProps: PropsFromDispatch = {
  toggleEditDefaultStandardScriptModal: actionCreators.ToggleEditDefaultStandardScriptModal,
  updateDefaultStandardScriptDraft: actionCreators.UpdateDefaultStandardScriptDraft,
  updateDraft: actionCreators.UpdateDefaultStandardScript,
  getDefaultStandardScriptContainer: actionCreators.GetDefaultStandardScript,
};

export default compose(
  localize(),
  connect(mapStateToProps, mapDispatchToProps),
)(DefaultScriptsSection);
